// Centralized playful dark design tokens.
// Keep this file lightweight: colors + shared sx helpers.

import { alpha } from '@mui/material';

export const BG_TOP = '#0f172a';
export const BG_BOTTOM = '#020617';
export const CARD = '#111827';
export const BORDER = 'rgba(255,255,255,0.08)';

export const TEXT_PRIMARY = '#f8fafc';
export const TEXT_SECONDARY = '#cbd5e1';

export const ACCENT = '#38bdf8';
export const ACCENT_2 = '#3b82f6';
export const ACCENT_3 = '#818cf8';
export const INFO = '#60a5fa';
export const SUCCESS = '#38bdf8';
export const WARNING = '#60a5fa';
export const DANGER = '#f87171';

/** Semantic table row action colors */
export const ACTION_VIEW = '#3b82f6';
export const ACTION_EDIT = '#f97316';
export const ACTION_DELETE = '#ef4444';

export const RADIUS_LG = '16px';
export const RADIUS_MD = '12px';
export const RADIUS_SM = '10px';

export const TRANSITION_180 = 'all 0.18s ease';

export const playfulGradient = `linear-gradient(135deg, ${ACCENT}, ${ACCENT_2}, ${ACCENT_3})`;
export const playfulSurface = `linear-gradient(145deg, ${alpha('#111827', 0.94)} 0%, ${alpha('#0f172a', 0.92)} 48%, ${alpha('#020617', 0.96)} 100%)`;
export const playfulBorder = (color = ACCENT, opacity = 0.24) => `1px solid ${alpha(color, opacity)}`;
export const playfulGlow = (color = ACCENT, opacity = 0.28) =>
  `0 18px 50px ${alpha('#000', 0.46)}, 0 0 28px ${alpha(color, opacity)}, inset 0 1px 0 ${alpha('#fff', 0.08)}`;

export const glassCardSx = {
  borderRadius: RADIUS_LG,
  background: playfulSurface,
  border: playfulBorder(ACCENT_2, 0.18),
  backdropFilter: 'blur(18px)',
  WebkitBackdropFilter: 'blur(18px)',
  boxShadow: playfulGlow(ACCENT_2, 0.12),
  position: 'relative',
  overflow: 'hidden',
};

export const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: RADIUS_MD,
    background: alpha('#020617', 0.38),
    color: TEXT_PRIMARY,
    '& fieldset': { borderColor: alpha(ACCENT_2, 0.2) },
    '&:hover fieldset': { borderColor: alpha(ACCENT, 0.36) },
    '&.Mui-focused fieldset': { borderColor: alpha(ACCENT, 0.58) },
  },
  '& .MuiInputLabel-root': { color: TEXT_SECONDARY, fontSize: 14 },
};

/** Dark-theme checkboxes for TableList row selection */
export const tableCheckboxSx = {
  p: 0.5,
  color: alpha(TEXT_SECONDARY, 0.7),
  '&.Mui-checked': { color: ACCENT },
  '&.MuiCheckbox-indeterminate': { color: ACCENT },
  '&.Mui-disabled': { color: alpha(TEXT_SECONDARY, 0.28) },
  '&:hover': { backgroundColor: alpha(ACCENT, 0.14) },
  '& .MuiSvgIcon-root': { fontSize: 20 },
};

export const bulkDialogPaperSx = {
  borderRadius: '18px',
  bgcolor: '#0f172a',
  border: playfulBorder(ACCENT, 0.24),
  backgroundImage: playfulSurface,
  boxShadow: playfulGlow(ACCENT, 0.2),
  overflow: 'visible',
};

export const bulkDialogFieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: RADIUS_MD,
    color: TEXT_PRIMARY,
    background: alpha('#111827', 0.82),
    '& fieldset': { borderColor: alpha(ACCENT_2, 0.22) },
    '&:hover fieldset': { borderColor: alpha(ACCENT, 0.4) },
    '&.Mui-focused fieldset': { borderColor: alpha(ACCENT, 0.58) },
  },
  '& .MuiInputLabel-root': {
    color: alpha(TEXT_SECONDARY, 0.85),
    zIndex: 1,
    '&.MuiInputLabel-shrink': {
      bgcolor: '#0f172a',
      px: 0.5,
      ml: -0.25,
    },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: alpha(ACCENT, 0.9) },
  '& .MuiAutocomplete-inputRoot': { overflow: 'visible' },
  '& .MuiOutlinedInput-input': {
    color: TEXT_PRIMARY,
    WebkitTextFillColor: TEXT_PRIMARY,
  },
  '& .MuiFormHelperText-root': {
    color: alpha(TEXT_SECONDARY, 0.92),
    fontSize: '0.8rem',
    marginTop: '6px',
    marginLeft: '2px',
  },
  '& .MuiSelect-icon': { color: alpha(ACCENT, 0.75) },
  '& input[type="date"]::-webkit-calendar-picker-indicator': {
    cursor: 'pointer',
    opacity: 1,
    filter: `brightness(0) saturate(100%) invert(72%) sepia(52%) saturate(1200%) hue-rotate(165deg) brightness(102%) contrast(101%)`,
  },
  '& input[type="date"]::-webkit-calendar-picker-indicator:hover': {
    filter: `brightness(0) saturate(100%) invert(80%) sepia(60%) saturate(1400%) hue-rotate(165deg) brightness(110%) contrast(101%)`,
  },
};

export const bulkDialogFieldDisabledSx = {
  '& .MuiOutlinedInput-root.Mui-disabled': {
    background: 'rgba(15, 23, 42, 0.60)',
    cursor: 'not-allowed',
    '& fieldset': { borderColor: 'rgba(71, 85, 105, 0.55) !important' },
    '&:hover fieldset': { borderColor: 'rgba(71, 85, 105, 0.55) !important' },
  },
  '& .MuiInputLabel-root.Mui-disabled': {
    color: 'rgba(148, 163, 184, 0.55) !important',
  },
  '& .MuiOutlinedInput-input.Mui-disabled': {
    color: 'rgba(148, 163, 184, 0.55) !important',
    WebkitTextFillColor: 'rgba(148, 163, 184, 0.55) !important',
  },
  '& input[type="date"]::-webkit-calendar-picker-indicator': {
    opacity: 0.35,
    cursor: 'not-allowed',
    filter: 'none',
  },
  '& input[type="date"]::-webkit-calendar-picker-indicator:hover': {
    filter: 'none',
  },
};

/** TableList shell + pagination nav (shared with attendance tables) */
export const TABLE_SURFACE = '#111827';
export const TABLE_SURFACE_2 = '#1e293b';
export const TABLE_SURFACE_3 = '#0d1525';

export const tableListShellSx = {
  borderRadius: RADIUS_LG,
  overflow: 'hidden',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  background: `linear-gradient(160deg, ${alpha(TABLE_SURFACE, 0.97)}, ${alpha(BG_TOP, 0.98)} 50%, ${alpha(TABLE_SURFACE_3, 0.99)})`,
  border: `1px solid ${alpha(ACCENT_2, 0.2)}`,
  boxShadow: `0 20px 60px ${alpha('#000', 0.48)}, 0 0 0 1px ${alpha(ACCENT, 0.06)}, inset 0 1px 0 ${alpha('#fff', 0.04)}`,
};

export const tableListHeaderBarSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  gap: 1.5,
  px: { xs: 2, sm: 2.5 },
  py: 1.1,
  borderBottom: `1px solid ${alpha(TEXT_PRIMARY, 0.06)}`,
  background: `linear-gradient(90deg, ${alpha(ACCENT, 0.08)} 0%, ${alpha(ACCENT_2, 0.04)} 60%, transparent 100%)`,
  position: 'relative',
  zIndex: 2,
};

export const tableListThSx = {
  fontFamily: "'DM Mono', 'Fira Mono', monospace",
  fontSize: '0.6rem',
  fontWeight: 600,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  color: alpha(TEXT_SECONDARY, 0.85),
  py: 1.4,
  bgcolor: alpha(TABLE_SURFACE_3, 0.95),
  borderBottom: `1px solid ${alpha(TEXT_PRIMARY, 0.07)}`,
  whiteSpace: 'nowrap',
};

export const tableNavButtonSx = (disabled = false) => ({
  width: 30,
  height: 30,
  borderRadius: '9px',
  border: `1px solid ${!disabled ? alpha(ACCENT, 0.5) : alpha(TEXT_SECONDARY, 0.22)}`,
  background: !disabled ? alpha(ACCENT, 0.14) : alpha(TEXT_SECONDARY, 0.08),
  color: !disabled ? TEXT_PRIMARY : alpha(TEXT_SECONDARY, 0.45),
  transition: 'all 0.16s ease',
  '&:hover:not(:disabled)': {
    background: alpha(ACCENT, 0.24),
    borderColor: alpha(ACCENT, 0.75),
    color: ACCENT,
    boxShadow: `0 0 14px ${alpha(ACCENT, 0.35)}`,
  },
  '& .MuiSvgIcon-root': {
    fontSize: 20,
    color: 'inherit',
    filter: !disabled ? `drop-shadow(0 0 5px ${alpha(ACCENT, 0.55)})` : 'none',
  },
  '&.Mui-disabled': { opacity: 0.7 },
});

export const bulkDialogMenuProps = {
  PaperProps: {
    sx: {
      bgcolor: '#111827',
      border: `1px solid ${alpha(ACCENT, 0.22)}`,
      '& .MuiMenuItem-root': {
        color: TEXT_PRIMARY,
        '&:hover': { bgcolor: alpha(ACCENT, 0.12) },
        '&.Mui-selected': { bgcolor: alpha(ACCENT, 0.2) },
      },
    },
  },
};
